/* eslint-disable import/order */
/* eslint-disable camelcase */
import { useState, useEffect } from 'react';

import {
  Card,
  Container,
  Typography,
  Box,
  Grid,
  CircularProgress
} from '@mui/material';

import { useTheme } from '@mui/material/styles';

import axios, { getErrorMessage } from 'utils/axios';
import useIsMountedRef from 'hooks/useIsMountedRef';
import CRUD from 'utils/Crud';

import { PATH_DASHBOARD } from '../../routes/paths';

import useSettings from '../../hooks/useSettings';

import Page from '../../components/Page';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Label from 'components/Label';

// =====================================================

export default function ComplianceOverview() {
  const { themeStretch } = useSettings();
  const isMountedRef = useIsMountedRef();
  const theme = useTheme();

  const [data, setData] = useState(null);

  const [state, setState] = useState({
    loading: false,
    error: null
  });

  useEffect(() => {
    getInitData();
  }, []);

  // =====================================================
  // FETCH DATA
  // =====================================================
  async function getInitData() {
    setState({ loading: true, error: null });

    try {
      const response = await axios.get(CRUD.compliance.overview);

      if (isMountedRef.current) {
        setData(response.data);
      }

      setState({ loading: false, error: null });
    } catch (error) {
      setState({
        loading: false,
        error: getErrorMessage(error)
      });
    }
  }

  // =====================================================
  // HELPERS
  // =====================================================

  function KPI({ title, value, color = 'default', subtitle }) {
    return (
      <Card sx={{ p: 3 }}>
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>

        <Typography variant="h4" sx={{ mt: 1 }}>
          {value ?? '-'}
        </Typography>

        {subtitle && (
          <Typography variant="caption" color="text.secondary">
            {subtitle}
          </Typography>
        )}

        <Box mt={1}>
          <Label color={color}>{title}</Label>
        </Box>
      </Card>
    );
  }

  function getRejectionColor(rate) {
    if (rate < 5) return 'success';
    if (rate < 15) return 'warning';
    return 'error';
  }

  // =====================================================

  return (
    <Page title="Compliance Overview">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Compliance Overview"
          links={[
            { name: 'Compliance', href: PATH_DASHBOARD.compliance.root },
            { name: 'Overview' },
          ]}
        />

        {/* ERROR */}
        {state.error && (
          <Card>
            <Box p={5} textAlign="center">
              <Typography color="error">{state.error}</Typography>
            </Box>
          </Card>
        )}

        {/* LOADING */}
        {state.loading && (
          <Box display="flex" justifyContent="center" mt={10}>
            <CircularProgress />
          </Box>
        )}

        {/* CONTENT */}
        {!state.loading && data && (
          <Grid container spacing={3}>

            {/* =========================
                TOP KPIs
            ========================= */}
            <Grid item xs={12} md={3}>
              <KPI
                title="Total Users"
                value={data.total_users}
                color="primary"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <KPI
                title="Pending Reviews"
                value={data.pending_reviews}
                color={data.pending_reviews > 0 ? 'warning' : 'success'}
                subtitle="Requires manual action"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <KPI
                title="Rejection Rate"
                value={`${data.rejection_rate || 0}%`}
                color={getRejectionColor(data.rejection_rate)}
              />
            </Grid>

            {/* <Grid item xs={12} md={3}>
              <KPI
                title="System Status"
                value="Healthy"
                color="success"
                subtitle="All systems nominal"
              />
            </Grid> */}

            {/* =========================
                STAGE BREAKDOWN
            ========================= */}
            <Grid item xs={12}>
              <Card sx={{ p: 3 }}>
                <Typography variant="h6">KYC Stage Progress</Typography>

                <Grid container spacing={2} mt={1}>
                  <Grid item xs={12} md={4}>
                    <StageCard
                      title="Stage One"
                      value={data.stage_one_completed}
                      color="success"
                      description="Identity Verified"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <StageCard
                      title="Stage Two"
                      value={data.stage_two_completed}
                      color="info"
                      description="Address Verified"
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <StageCard
                      title="Stage Three"
                      value={data.stage_three_completed}
                      color="secondary"
                      description="Source of Funds"
                    />
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            {/* =========================
                INSIGHT PANEL
            ========================= */}
            <Grid item xs={12}>
              <Card sx={{ p: 3 }}>
                <Typography variant="h6">Operational Insight</Typography>

                <Box mt={2}>
                  {data.pending_reviews > 20 && (
                    <Label color="error">
                      High backlog in compliance queue
                    </Label>
                  )}

                  {data.rejection_rate > 15 && (
                    <Box mt={1}>
                      <Label color="warning">
                        Elevated rejection rate detected
                      </Label>
                    </Box>
                  )}

                  {data.pending_reviews === 0 && (
                    <Box mt={1}>
                      <Label color="success">
                        All reviews up to date
                      </Label>
                    </Box>
                  )}
                </Box>
              </Card>
            </Grid>

          </Grid>
        )}
      </Container>
    </Page>
  );
}

// =====================================================
// STAGE CARD
// =====================================================

function StageCard({ title, value, description, color }) {
  return (
    <Card
      sx={{
        p: 2,
        borderLeft: `4px solid`,
        borderColor: `${color}.main`
      }}
    >
      <Typography variant="subtitle2">{title}</Typography>

      <Typography variant="h5" mt={1}>
        {value ?? '-'}
      </Typography>

      <Typography variant="caption" color="text.secondary">
        {description}
      </Typography>
    </Card>
  );
}